import { useCallback, useEffect, useMemo, useState } from 'react';
import { DiscoveryApi } from '@backstage/core-plugin-api';

export const AUTH_TIMEOUT_MS = 30000;
export const POLL_MS = 10000;

import { makeStyles } from '@material-ui/core';

export const useSignInStyles = makeStyles(() => ({
  mainContainer: {
    display: 'flex',
    marginBlock: '10rem',
    alignItems: 'center',
    width: 'fit-content',
  },
  progressWrapper: {
    marginTop: '10rem',
    textAlign: 'center',
  },
  connectingText: {
    marginTop: '1rem',
  },
  overrideMuiMain: {
    '& .MuiContainer-root main': {
      all: 'unset !important',
      display: 'block !important',
      padding: '20px !important',
    },

  },
}));

export const buildLdapRefreshUrl = async (
  discoveryApi: DiscoveryApi,
): Promise<string> => {
  const baseUrl = await discoveryApi.getBaseUrl('auth');
  return `${baseUrl}`;
};

export const pingWithTimeout = async (
  url: string,
  timeoutMs: number = AUTH_TIMEOUT_MS,
  fetchImpl: typeof fetch = fetch,
  logger: Pick<Console, 'log' | 'error'> = console,
): Promise<boolean> => {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    logger.log(`[LDAP Refresh] Pinging backend: ${url}`);
    const res = await fetchImpl(url, {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal,
    });
    logger.log(`[LDAP Refresh] Ping status: ${res.status} ${res.statusText}`);
    return true;
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      logger.error('[LDAP Refresh] Ping timed out');
    } else {
      logger.error('[LDAP Refresh] Ping network/request error:', e);
    }
    return false;
  } finally {
    globalThis.clearTimeout(timer);
  }
};

export const startPoller = (
  probe: () => Promise<boolean>,
  intervalMs: number,
  onSuccess: () => void,
  logger: Pick<Console, 'log'> = console,
): (() => void) => {
  let stopped = false;

  const tick = async () => {
    const ok = await probe();
    if (!stopped && ok) {
      logger.log(
        '[LDAP Refresh][Poll] Backend reachable. Reloading page to continue sign-in.',
      );
      onSuccess();
    }
  };

  void tick();
  const id = globalThis.setInterval(tick, intervalMs);

  return () => {
    stopped = true;
    globalThis.clearInterval(id);
    logger.log('[LDAP Refresh][Poll] Stopped polling');
  };
};

export type AuthStatus = 'loading' | 'error' | 'success';

export interface UseLdapRefreshResult {
  status: AuthStatus;
  showHelp: boolean;
  handleSignInError: (error: unknown) => Promise<void>;
}

export const useLdapRefresh = (
  discoveryApi: DiscoveryApi,
  locationRef: Location = globalThis.window.location,
): UseLdapRefreshResult => {
  const [status, setStatus] = useState<AuthStatus>('loading');

  const getRefreshUrl = useCallback(async () => {
    return buildLdapRefreshUrl(discoveryApi);
  }, [discoveryApi]);

  const pingAuth = useCallback(async (): Promise<boolean> => {
    try {
      const url = await getRefreshUrl();
      return await pingWithTimeout(url);
    } catch {
      return false;
    }
  }, [getRefreshUrl]);

  useEffect(() => {
    let aborted = false;
    (async () => {
      const ok = await pingAuth();
      if (aborted) return;
      setStatus(ok ? 'success' : 'error');
      console.log(
        ok
          ? '[LDAP Refresh] Backend responded. Hiding help.'
          : '[LDAP Refresh] Unreachable. Showing help.',
      );
    })();
    return () => {
      aborted = true;
      console.log('[LDAP Refresh] Cleanup (initial)');
    };
  }, [pingAuth]);

  useEffect(() => {
    if (status !== 'error') return;

    const cleanup = startPoller(pingAuth, POLL_MS, () =>
      locationRef.reload(),
    );

    return cleanup;
  }, [status, pingAuth]);

  const handleSignInError = useCallback(
    async (error: unknown) => {
      console.error('[LDAP Sign-In] Error during sign-in:', error);
      const ok = await pingAuth();
      if (ok) {
        console.log(
          '[LDAP Sign-In] Backend reachable post-error. Keeping sign-in view.',
        );
        return;
      }
      console.log(
        '[LDAP Sign-In] Connectivity failed post-error. Showing help.',
      );
      setStatus('error');
    },
    [pingAuth],
  );

  return useMemo(
    () => ({ status, showHelp: status === 'error', handleSignInError }),
    [status, handleSignInError],
  );
};
